/**
 * `/settings/profile` — user profile.
 *
 * Clerk is the source of truth for every field here. We wire the
 * user's first/last name to `user.update(...)`, show the email
 * as read-only (verified addresses live on Clerk's account page),
 * and use Clerk's built-in avatar uploader.
 */
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { mapClerkError } from '@/lib/mapClerkError';
import { useUser } from '@clerk/react';
import { Trash, UploadSimple, UserCircle } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import {
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';

export const Route = createFileRoute('/_app/settings/profile')({
  component: ProfilePage,
});

function ProfilePage(): ReactElement {
  const { user, isLoaded } = useUser();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
  }, [user]);

  if (!isLoaded || !user) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-20 w-64" />
        <Skeleton className="h-36 w-full max-w-md" />
      </div>
    );
  }

  const primaryEmail =
    user.primaryEmailAddress?.emailAddress ?? user.emailAddresses[0]?.emailAddress ?? '—';

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;
    setFieldErrors({});
    setFormError(undefined);
    setSubmitting(true);
    try {
      await user.update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      toastSuccess('Profile updated');
    } catch (err) {
      const mapped = mapClerkError(err);
      setFieldErrors(mapped.fieldErrors);
      setFormError(mapped.formError);
    } finally {
      setSubmitting(false);
    }
  }

  async function onAvatarChange(event: ChangeEvent<HTMLInputElement>) {
    if (!user) return;
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarBusy(true);
    try {
      await user.setProfileImage({ file });
      toastSuccess('Avatar updated');
    } catch (err) {
      toastFromError(err);
    } finally {
      setAvatarBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function onAvatarClear() {
    if (!user) return;
    if (!window.confirm('Remove your avatar?')) return;
    setAvatarBusy(true);
    try {
      await user.setProfileImage({ file: null });
      toastSuccess('Avatar removed');
    } catch (err) {
      toastFromError(err);
    } finally {
      setAvatarBusy(false);
    }
  }

  return (
    <div className="max-w-xl space-y-8">
      <section className="space-y-3">
        <header>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Avatar</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Shown everywhere alongside your name. PNG or JPG, ≤2MB.
          </p>
        </header>
        <div className="flex items-center gap-4">
          <div className="flex size-16 items-center justify-center overflow-hidden rounded-full border border-[var(--color-line)] bg-[var(--color-paper-alt)]">
            {user.imageUrl ? (
              <img src={user.imageUrl} alt="" className="size-full object-cover" />
            ) : (
              <UserCircle aria-hidden="true" size={36} className="text-[var(--color-mute)]" />
            )}
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={onAvatarChange}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarBusy}
            >
              {avatarBusy ? <Spinner className="size-4" /> : <UploadSimple aria-hidden="true" />}
              Upload image
            </Button>
            {user.imageUrl && (
              <Button
                variant="ghost"
                onClick={() => {
                  void onAvatarClear();
                }}
                disabled={avatarBusy}
              >
                <Trash aria-hidden="true" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </section>

      <div className="h-px w-full bg-[var(--color-line)]" />

      <section>
        <header className="mb-4">
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Name</h2>
          <p className="text-sm text-[var(--color-mute)]">
            How teammates see you in audits, comments, and invite lists.
          </p>
        </header>
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="first-name">First name</FieldLabel>
              <Input
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName((e.target as HTMLInputElement).value)}
                autoComplete="given-name"
              />
              {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
            </Field>
            <Field>
              <FieldLabel htmlFor="last-name">Last name</FieldLabel>
              <Input
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName((e.target as HTMLInputElement).value)}
                autoComplete="family-name"
              />
              {fieldErrors.lastName && <FieldError>{fieldErrors.lastName}</FieldError>}
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input id="email" value={primaryEmail} readOnly disabled />
            <p className="text-xs text-[var(--color-mute)]">
              Email changes happen in Clerk's secure account settings — we don't handle them in-app.
            </p>
          </Field>
          {formError && (
            <div
              className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
              role="alert"
            >
              {formError}
            </div>
          )}
          <Button type="submit" loading={submitting}>
            Save changes
          </Button>
        </form>
      </section>
    </div>
  );
}
